/* ══════════════════════════════════════════════════════════════════════
   SYSTEM OWNER DASHBOARD — live GRC API adapters
   This file used to reshape one shared rollup call; that endpoint 404s and
   is gone. Each widget now fetches its own dedicated system-owner endpoint,
   and the build* function below reshapes that one response into the shape
   its widget renders, so the widgets stay simple render components:

     buildApplicationCompliance  <- /system-owner/control-compliance
     buildRemediationStatus      <- /system-owner/remediation-status
     buildComplianceTrend        <- /system-owner/compliance-trend

   Controls Yet to be Assessed and Controls with Highest Failure Rate need no
   adapter — their endpoints return render-ready, server-sorted rows. What
   remains for them here is shared presentation constants (the severity
   palette).
   ══════════════════════════════════════════════════════════════════════ */
import type {
  GrcRemediationItemRef,
  GrcRemediationBucketCounts,
  SystemOwnerRemediationAppRow,
  SystemOwnerControlSeverity,
  SystemOwnerControlComplianceAppRow,
  SystemOwnerComplianceTrendAppRow,
} from "../../../../api/frameworksApi";

/* Fixed status/categorical hex constants per the platform's data-viz method:
   color assigned by role (status = pass/fail/severity semantics, categorical
   = plan stages / non-ordinal series), never cycled or reused across roles. */
export const STATUS_HEX = {
  good: "#10b981",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",
} as const;
export type StatusKey = keyof typeof STATUS_HEX;

export const CATEGORICAL_HEX = {
  series1: "#2a78d6",
  series2: "#eb6834",
  series3: "#1baf7a",
  series4: "#eda100",
} as const;

export const NOT_IDENTIFIED_HEX = "#94a3b8"; // slate-400 — neutral, not pass or fail

/* Band thresholds are on the RATE ITSELF, not raw magnitude — failure-rate
   framing bands high=bad, compliance framing inverts the same bands so
   high=good. */
export function rateBandHex(rate: number, mode: "failure" | "compliance"): string {
  const r = mode === "failure" ? rate : 1 - rate;
  if (r >= 0.7) return STATUS_HEX.critical;
  if (r >= 0.4) return STATUS_HEX.serious;
  if (r >= 0.15) return STATUS_HEX.warning;
  return STATUS_HEX.good;
}

/* ── Application Compliance Score ──
   passed + failed + notIdentified (= notReviewedControls) always equals
   coveredControls — NOT totalControls. totalControls/coveragePercent are the
   separate "how much of the baseline is even covered" metric. */
export interface SspComplianceSummary {
  sspId: string;
  sspName: string;
  /** User-supplied short code (e.g. "PAY-CORE-V3"). Nullable. */
  shortCode: string | null;
  applicationName: string;
  totalControls: number;
  coveredControls: number;
  passed: number;
  failed: number;
  notIdentified: number;
  compliancePercent: number;
  coveragePercent: number;
}
export interface ApplicationComplianceSummary {
  applicationId: string;
  applicationName: string;
  /** Published SSPs linked to the app. 0 → no SSP published yet. */
  sspCount: number;
  ssps: SspComplianceSummary[];
  totalControls: number;
  coveredControls: number;
  passed: number;
  failed: number;
  notIdentified: number;
  compliancePercent: number;
  coveragePercent: number;
}

/** Falls back to deriving "not reviewed" from covered/passed/failed if the API
 *  response doesn't include notReviewedControls yet (older backend build). */
function notReviewedOf(s: { notReviewedControls?: number; coveredControls: number; passedControls: number; failedControls: number }): number {
  return s.notReviewedControls ?? Math.max(0, s.coveredControls - s.passedControls - s.failedControls);
}

export function buildApplicationCompliance(apps: SystemOwnerControlComplianceAppRow[]): ApplicationComplianceSummary[] {
  return apps.map((app) => {
    const t = app.totals;
    const ssps: SspComplianceSummary[] = (app.ssps ?? []).map((s) => ({
      sspId: s.sspId,
      sspName: s.name,
      shortCode: s.shortCode,
      applicationName: app.applicationName,
      totalControls: s.totalControlsInSsp,
      coveredControls: s.coveredControls,
      passed: s.passedControls,
      failed: s.failedControls,
      notIdentified: notReviewedOf(s),
      compliancePercent: s.compliancePercent,
      coveragePercent: s.coveragePercent,
    }));
    return {
      applicationId: app.applicationId,
      applicationName: app.applicationName,
      sspCount: app.sspCount ?? 0,
      ssps,
      totalControls: t.totalControlsInSsp,
      coveredControls: t.coveredControls,
      passed: t.passedControls,
      failed: t.failedControls,
      notIdentified: notReviewedOf(t),
      compliancePercent: t.compliancePercent,
      coveragePercent: t.coveragePercent,
    };
  });
}

/* ── Failed Control Remediation Status — each bucket is now a
   {count, items[]} object carrying the actual POA&M items behind the number,
   so the table/drill-down renders real per-control rows straight from this
   one payload (no follow-up call needed). Bucket priority order is fixed:
   overdue -> noRemediationPlan -> inActiveRemediation. */
export const REMEDIATION_STATUS_ORDER = ["overdue", "noPlan", "inRemediation"] as const;
export type RemediationStatusKey = (typeof REMEDIATION_STATUS_ORDER)[number];

export interface RemediationItem {
  poamItemId: string;
  sspId: string;
  sspName: string;
  applicationName: string;
  controlId: string | null;
  controlTitle: string | null;
  failedSince: string;
  dueDate: string | null;
  daysUntilDue: number | null;
  status: RemediationStatusKey;
}

export interface SspRemediationRow {
  sspId: string;
  sspName: string;
  applicationName: string;
  overdue: number;
  noPlan: number;
  inRemediation: number;
  items: RemediationItem[];
}
export interface AppRemediationSummary {
  applicationId: string;
  applicationName: string;
  /** Published SSPs linked to the app. 0 → no SSP published yet. */
  sspCount: number;
  overdue: number;
  noPlan: number;
  inRemediation: number;
  ssps: SspRemediationRow[];
  /** Sourced from the app's own totals items — NOT a flatten of ssps[]'s
      items, per the API contract (the two should agree, but totals is authoritative
      for the app-level view). */
  items: RemediationItem[];
}

function mapRemediationItem(
  it: GrcRemediationItemRef,
  status: RemediationStatusKey,
  applicationName: string,
  sspNameById: Map<string, string>,
): RemediationItem {
  return {
    poamItemId: it.poamItemId,
    sspId: it.sspId,
    sspName: sspNameById.get(it.sspId) ?? "Unknown SSP",
    applicationName,
    controlId: it.controlId,
    controlTitle: it.controlTitle,
    failedSince: it.failedSince,
    dueDate: it.dueDate,
    daysUntilDue: it.daysUntilDue,
    status,
  };
}

/** Tolerates the older plain-number bucket shape (or a missing bucket entirely)
 *  in case the backend hasn't rolled out the {count, items[]} shape everywhere yet. */
function bucketCount(b: number | { count: number } | undefined | null): number {
  if (typeof b === "number") return b;
  return b?.count ?? 0;
}
function bucketItems(b: { items?: GrcRemediationItemRef[] } | number | undefined | null): GrcRemediationItemRef[] {
  if (!b || typeof b === "number") return [];
  return b.items ?? [];
}

function mapBucketCounts(applicationName: string, sspNameById: Map<string, string>, b: GrcRemediationBucketCounts | undefined) {
  return {
    overdue: bucketCount(b?.overdue),
    noPlan: bucketCount(b?.noRemediationPlan),
    inRemediation: bucketCount(b?.inActiveRemediation),
    items: [
      ...bucketItems(b?.overdue).map((it) => mapRemediationItem(it, "overdue", applicationName, sspNameById)),
      ...bucketItems(b?.noRemediationPlan).map((it) => mapRemediationItem(it, "noPlan", applicationName, sspNameById)),
      ...bucketItems(b?.inActiveRemediation).map((it) => mapRemediationItem(it, "inRemediation", applicationName, sspNameById)),
    ],
  };
}

/** Bucket priority first, then oldest-failed-first within a bucket — matches the
    server's own sort within each bucket, extended to a stable cross-bucket order. */
export function sortRemediationItems(items: RemediationItem[]): RemediationItem[] {
  const rank: Record<RemediationStatusKey, number> = { overdue: 0, noPlan: 1, inRemediation: 2 };
  return [...items].sort(
    (a, b) => rank[a.status] - rank[b.status] || new Date(a.failedSince).getTime() - new Date(b.failedSince).getTime(),
  );
}

export function buildRemediationStatus(apps: SystemOwnerRemediationAppRow[]): {
  appSummaries: AppRemediationSummary[];
  totals: { noPlan: number; overdue: number; inRemediation: number };
} {
  const appSummaries: AppRemediationSummary[] = apps.map((app) => {
    const sspRows = app.ssps ?? [];
    const sspNameById = new Map(sspRows.map((s) => [s.sspId, s.name]));
    const ssps: SspRemediationRow[] = sspRows.map((s) => {
      const counts = mapBucketCounts(app.applicationName, sspNameById, s);
      return { sspId: s.sspId, sspName: s.name, applicationName: app.applicationName, ...counts };
    });
    const totalsCounts = mapBucketCounts(app.applicationName, sspNameById, app.totals);
    return {
      applicationId: app.applicationId,
      applicationName: app.applicationName,
      sspCount: app.sspCount ?? 0,
      overdue: totalsCounts.overdue,
      noPlan: totalsCounts.noPlan,
      inRemediation: totalsCounts.inRemediation,
      items: totalsCounts.items,
      ssps,
    };
  });
  const totals = appSummaries.reduce(
    (acc, a) => ({ noPlan: acc.noPlan + a.noPlan, overdue: acc.overdue + a.overdue, inRemediation: acc.inRemediation + a.inRemediation }),
    { noPlan: 0, overdue: 0, inRemediation: 0 },
  );
  return { appSummaries, totals };
}

/* ── "Controls with Highest Failure Rate" — rows now come straight from the
   dedicated GET /dashboard/grc/system-owner/control-failure-rates endpoint
   (see fetchControlFailureRates). It returns a flat control list already
   sorted severity desc -> failureRate desc -> failures desc -> controlId asc,
   so there is no client-side aggregation or sorting left to do. What remains
   here is the severity palette shared by the widget's legend, treemap tiles
   and badges.

   severity is the highest POA&M severity across the control's items in scope
   (worst-case per control, not per plan/SSP) and is independent of
   failureRate — a LOW control can still fail 100% of the time, so the UI
   shows both. */
export type ControlSeverity = SystemOwnerControlSeverity;

/** Legend order mirrors the server's sort: worst first, unrated last. */
export const SEVERITY_ORDER: (ControlSeverity | null)[] = ["CRITICAL", "HIGH", "MEDIUM", "LOW", null];

/** Labels are stored properly cased so nothing has to force casing in CSS. */
export function severityLabel(s: ControlSeverity | null): string {
  switch (s) {
    case "CRITICAL":
      return "Critical";
    case "HIGH":
      return "High";
    case "MEDIUM":
      return "Medium";
    case "LOW":
      return "Low";
    default:
      return "No Severity";
  }
}

export function severityHex(s: ControlSeverity | null): string {
  switch (s) {
    case "CRITICAL":
      return STATUS_HEX.critical;
    case "HIGH":
      return STATUS_HEX.serious;
    case "MEDIUM":
      return STATUS_HEX.warning;
    case "LOW":
      return STATUS_HEX.good;
    default:
      return NOT_IDENTIFIED_HEX;
  }
}

/* ── "Controls Yet to be Assessed" — the rows come straight from the dedicated
   GET /dashboard/grc/system-owner/unassessed-controls endpoint (see
   fetchUnassessedControls), so no adapter is needed. This is only the shape of
   the Application/SSP options feeding that widget's filter dropdowns. The
   Application options come from fetchAccessibleApplications (GET
   /users/current/accessible-organizations?org_type=APPLICATION); that endpoint
   has no per-application SSP list, so ssps stays empty until a per-application
   SSP source is wired up. */
export interface AppSspOption {
  applicationId: string;
  applicationName: string;
  ssps: { sspId: string; sspName: string }[];
}

/* ── Application Compliance Trend — one point per calendar month
   (trailing 12-month window), straight from the API's complianceTrend
   section per application. Coverage isn't tracked month-over-month by the
   API (only the live compliance snapshot is), so the per-cycle detail below
   shows Compliance/Passed/Failed/Diff instead of a Coverage column. */
export interface ComplianceTrendPoint {
  month: string;
  monthLabel: string;
  compliancePercent: number;
  passedControls: number;
  failedControls: number;
  diffFromPreviousMonth: number | null;
}
export interface AppComplianceTrend {
  applicationId: string;
  applicationName: string;
  /** 0 → no published SSP; the series is expected to sit flat at 0%. */
  sspCount: number;
  points: ComplianceTrendPoint[];
}

function formatMonthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

export function buildComplianceTrend(apps: SystemOwnerComplianceTrendAppRow[]): AppComplianceTrend[] {
  return apps.map((app) => ({
    applicationId: app.applicationId,
    applicationName: app.applicationName,
    sspCount: app.sspCount ?? 0,
    points: (app.months ?? []).map((m) => ({
      month: m.month,
      monthLabel: formatMonthLabel(m.month),
      compliancePercent: m.compliancePercent,
      passedControls: m.passedControls,
      failedControls: m.failedControls,
      diffFromPreviousMonth: m.diffFromPreviousMonth,
    })),
  }));
}
